import { Stack } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useRef, useState } from "react";
import { ReportAggregationPeriod } from "../../../generated/graphql";
import { type DateOption } from "../../date-range-picker";
import { PIPELINE_LABELS } from "../../doc-ingestion/pipeline-filter";
import TabPanel from "../../common/tab-panel/tab-panel";
import { DocScanVolumeByPipelineHeader } from "./doc-scan-volume-by-pipeline-header";
import {
  type DocScanVolumeRow,
  PIPELINES,
  useDocScanVolumeByPipeline,
} from "./use-doc-scan-volume-by-pipeline";
import { StatisticsView } from "./utils";

const styles = {
  root: { overflowY: "scroll" },
} satisfies Record<string, SxProps<Theme>>;

const dateRangeTemplate = (row: DocScanVolumeRow) => (
  <span>
    {dayjs(row.startDate).format("MM/DD")}-
    {dayjs(row.endDate).format("MM/DD/YY")}
  </span>
);

/** Displays document scan volume for both ingestion pipelines. */
const DocScanVolumeByPipelineStatistics = () => {
  const tableRef = useRef<DataTable<DocScanVolumeRow[]>>(null);
  const [view, setView] = useState<StatisticsView>(StatisticsView.CHART);
  const [period, setPeriod] = useState<ReportAggregationPeriod>(
    ReportAggregationPeriod.Week,
  );
  const [dateOption, setDateOption] = useState<DateOption>({
    startDate: dayjs(new Date()).subtract(30, "days").toDate(),
    endDate: null,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const { chartData, chartRowsPerPage, loading, page, setPage, volumeData } =
    useDocScanVolumeByPipeline({ dateOption, isExpanded, period });

  return (
    <Stack sx={styles.root}>
      <div
        style={
          isExpanded
            ? {
                position: "absolute",
                zIndex: 10_000,
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "white",
              }
            : {}
        }
      >
        <DocScanVolumeByPipelineHeader
          chartRowsPerPage={chartRowsPerPage}
          dateOption={dateOption}
          isExpanded={isExpanded}
          page={page}
          period={period}
          totalRows={volumeData?.length ?? 0}
          view={view}
          onDateOptionChange={setDateOption}
          onExpandedChange={() => {
            setIsExpanded((previous) => !previous);
          }}
          onExport={() => {
            tableRef.current?.exportCSV();
          }}
          onPageChange={setPage}
          onPeriodChange={setPeriod}
          onViewChange={setView}
        />
        <TabPanel panelValue={StatisticsView.CHART} selectedValue={view}>
          <Chart
            height={isExpanded ? "90vh" : "320px"}
            type="bar"
            data={chartData}
            options={{
              indexAxis: "x",
              maintainAspectRatio: false,
              aspectRatio: 0.8,
              scales: {
                x: {
                  ticks: { font: { weight: 500 }, precision: 0 },
                  grid: { display: false, drawBorder: false },
                },
                y: { grid: { drawBorder: false } },
              },
            }}
          />
        </TabPanel>
        <TabPanel panelValue={StatisticsView.TABLE} selectedValue={view}>
          <DataTable
            ref={tableRef}
            key={`open-${isExpanded}`}
            removableSort
            showGridlines
            scrollable
            stripedRows
            value={volumeData}
            sortMode="multiple"
            rows={10}
            loading={loading}
            dataKey="startDate"
            emptyMessage="No data"
            size="small"
            scrollHeight={isExpanded ? "100vh" : "325px"}
            virtualScrollerOptions={{ itemSize: 25 }}
          >
            <Column
              sortable
              field="startDate"
              header="Start Date"
              body={dateRangeTemplate}
            />
            {PIPELINES.map((pipeline) => (
              <Column
                key={pipeline}
                sortable
                header={PIPELINE_LABELS[pipeline]}
                field={pipeline}
              />
            ))}
            <Column sortable header="Total" field="total" />
          </DataTable>
        </TabPanel>
      </div>
    </Stack>
  );
};

export default DocScanVolumeByPipelineStatistics;
